import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Tabs,
  Select,
  Spin,
} from "antd";
import {
  DollarOutlined,
  FileTextOutlined,
  TeamOutlined,
  WarningOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Navigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import {
  fetchBusinessDashboard,
  getProductionReport,
} from "../../api/dashboardApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

const BusinessDashboard = () => {
  // ── Auth ───────────────────────────────────────────────────
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  // ── State ──────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [orderPipeline, setOrderPipeline] = useState(null);
  const [customerSales, setCustomerSales] = useState(null);
  const [dailyProduction, setDailyProduction] = useState([]);

  // ── Compute date range from period ─────────────────────────
  const getDateRange = useCallback(() => {
    const toDate = moment().format("YYYY-MM-DD");
    let fromDate;
    switch (period) {
      case "week":
        fromDate = moment().subtract(7, "days").format("YYYY-MM-DD");
        break;
      case "quarter":
        fromDate = moment().subtract(3, "months").format("YYYY-MM-DD");
        break;
      case "year":
        fromDate = moment().startOf("year").format("YYYY-MM-DD");
        break;
      case "month":
      default:
        fromDate = moment().subtract(1, "month").format("YYYY-MM-DD");
        break;
    }
    return { fromDate, toDate };
  }, [period]);

  // ── Fetch data ─────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    const { fromDate, toDate } = getDateRange();
    try {
      const [biz, prodReport] = await Promise.allSettled([
        fetchBusinessDashboard(fromDate, toDate),
        getProductionReport({ from_date: fromDate, to_date: toDate }),
      ]);

      if (biz.status === "fulfilled") {
        setOrderPipeline(biz.value.orderPipeline);
        setCustomerSales(biz.value.customerSales);
      }
      if (prodReport.status === "fulfilled") {
        setDailyProduction(prodReport.value?.daily_production || []);
      }
    } catch (error) {
      toast.error("Failed to load business dashboard");
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Auth guard (after hooks) ───────────────────────────────
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  checkTokenExp(accessToken);

  // ── Derived metrics ────────────────────────────────────────
  const totalOrderValue = (orderPipeline?.orders_by_status || []).reduce(
    (s, o) => s + (o.value || 0),
    0
  );
  const totalOrderCount = (orderPipeline?.orders_by_status || []).reduce(
    (s, o) => s + (o.count || 0),
    0
  );
  const quotationMetrics = orderPipeline?.quotation_metrics || {};
  const upcomingDeliveries = orderPipeline?.upcoming_deliveries || [];
  const customers = customerSales?.customers || [];

  // ── Quotation status pie data ──────────────────────────────
  const quotationPieData = [];
  if (quotationMetrics.converted) {
    quotationPieData.push({
      name: "Converted",
      value: quotationMetrics.converted,
    });
  }
  if (
    quotationMetrics.total_quotations_90_days &&
    quotationMetrics.converted
  ) {
    quotationPieData.push({
      name: "Pending / Lost",
      value:
        quotationMetrics.total_quotations_90_days -
        quotationMetrics.converted,
    });
  }

  // Order status pie
  const orderStatusPie = (orderPipeline?.orders_by_status || []).map(
    (o) => ({
      name: (o.status || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase()),
      value: o.count,
    })
  );

  // ── Daily production chart data ────────────────────────────
  const dailyChartData = dailyProduction.map((d) => ({
    date: moment(d.date).format("DD MMM"),
    Produced: d.produced || 0,
    Rejected: d.rejected || 0,
  }));

  // ── Upcoming delivery columns ──────────────────────────────
  const deliveryColumns = [
    {
      title: "Order",
      dataIndex: "order_number",
      key: "order_number",
      render: (val) => <Link to={`/order/${val}`}>{val}</Link>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => {
        const color =
          val === "in_production"
            ? "blue"
            : val === "confirmed"
            ? "green"
            : "default";
        return (
          <Tag color={color}>
            {(val || "").replace(/_/g, " ").toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Delivery",
      dataIndex: "expected_delivery",
      key: "expected_delivery",
      render: (val) => (val ? moment(val).format("DD-MMM-YYYY") : "—"),
    },
    {
      title: "Amount (₹)",
      dataIndex: "total_amount",
      key: "total_amount",
      align: "right",
      render: (v) => `₹${(v || 0).toLocaleString()}`,
    },
  ];

  // ── Top customers columns ──────────────────────────────────
  const customerColumns = [
    {
      title: "Customer ID",
      dataIndex: "customer_id",
      key: "customer_id",
    },
    {
      title: "Orders",
      dataIndex: "order_count",
      key: "order_count",
      align: "right",
    },
    {
      title: "Total Value (₹)",
      dataIndex: "total_value",
      key: "total_value",
      align: "right",
      render: (v) => `₹${(v || 0).toLocaleString()}`,
      sorter: (a, b) => (a.total_value || 0) - (b.total_value || 0),
      defaultSortOrder: "descend",
    },
    {
      title: "Dispatched (₹)",
      dataIndex: "dispatched_value",
      key: "dispatched_value",
      align: "right",
      render: (v) => `₹${(v || 0).toLocaleString()}`,
    },
  ];

  // ── Render ─────────────────────────────────────────────────
  return (
    <>
      <PageTitle title="Back" subtitle="Business Dashboard" />

      {/* Period selector */}
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center" }}>
        <DollarOutlined style={{ fontSize: 22, marginRight: 8 }} />
        <span style={{ fontSize: 18, fontWeight: 600, marginRight: 16 }}>
          Business Overview
        </span>
        <Select
          value={period}
          onChange={setPeriod}
          style={{ width: 140 }}
        >
          <Select.Option value="week">This Week</Select.Option>
          <Select.Option value="month">This Month</Select.Option>
          <Select.Option value="quarter">This Quarter</Select.Option>
          <Select.Option value="year">This Year</Select.Option>
        </Select>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 80 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Order Value"
                  value={totalOrderValue}
                  prefix="₹"
                  valueStyle={{ color: "#52c41a" }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Orders"
                  value={totalOrderCount}
                  prefix={<FileTextOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Quotation Conversion"
                  value={quotationMetrics.conversion_rate || 0}
                  suffix="%"
                  prefix={<RiseOutlined />}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Customers"
                  value={customerSales?.total_customers || 0}
                  prefix={<TeamOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={16}>
              <Card title="Daily Production Trend">
                {dailyChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dailyChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip
                        formatter={(value) => value.toLocaleString()}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="Produced"
                        stroke="#1890ff"
                        strokeWidth={2}
                        name="Produced"
                      />
                      <Line
                        type="monotone"
                        dataKey="Rejected"
                        stroke="#f5222d"
                        strokeWidth={2}
                        name="Rejected"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div
                    style={{
                      height: 300,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#999",
                    }}
                  >
                    No production data for this period
                  </div>
                )}
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card title="Order Status Breakdown">
                {orderStatusPie.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={orderStatusPie}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={90}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {orderStatusPie.map((_, index) => (
                          <Cell
                            key={index}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div
                    style={{
                      height: 300,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#999",
                    }}
                  >
                    No order data
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Tabs */}
          <Card>
            <Tabs
              items={[
                {
                  key: "upcoming",
                  label: (
                    <span>
                      <WarningOutlined /> Upcoming Deliveries (
                      {upcomingDeliveries.length})
                    </span>
                  ),
                  children: (
                    <Table
                      columns={deliveryColumns}
                      dataSource={upcomingDeliveries}
                      rowKey={(r) => r.order_number || Math.random()}
                      pagination={{ pageSize: 10 }}
                      size="small"
                      locale={{ emptyText: "No upcoming deliveries" }}
                    />
                  ),
                },
                {
                  key: "top-customers",
                  label: (
                    <span>
                      <TeamOutlined /> Customer Sales
                    </span>
                  ),
                  children: (
                    <Table
                      columns={customerColumns}
                      dataSource={customers}
                      rowKey={(r) => r.customer_id}
                      pagination={{ pageSize: 10 }}
                      size="small"
                      locale={{ emptyText: "No customer data" }}
                    />
                  ),
                },
              ]}
            />
          </Card>
        </>
      )}
    </>
  );
};

export default BusinessDashboard;
